/**
 * Composable for handling form validation error scrolling
 * Provides utilities to scroll to validation errors and payment sections
 */

export function useFormScrolling() {
  /**
   * Scrolls to the first form validation error
   * Handles dynamic content like Stripe country blocks that appear after entering CC details
   */
  function scrollToValidationError() {
    // Wait for next tick to ensure DOM is updated with validation errors
    setTimeout(() => {
      const attemptScroll = (attempt = 0) => {
        // Find the first form item with validation error
        const errorElement = document.querySelector('.el-form-item.is-error');

        if (errorElement) {
          // Calculate scroll position with larger offset to account for dynamic content
          // (like Stripe country selection that appears after entering CC details)
          const headerOffset = 100; // Increased offset for dynamic content
          const elementPosition = errorElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          // Smooth scroll to the error element
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });

          // Focus on the input field within the error element for better accessibility
          const inputElement = errorElement.querySelector('input');
          if (inputElement) {
            setTimeout(() => {
              inputElement.focus();
            }, 500); // Wait for scroll animation to complete
          }
        } else if (attempt === 0) {
          // If no error element found on first attempt, try again after a longer delay
          // This handles cases where validation errors take time to appear in DOM
          setTimeout(() => attemptScroll(1), 200);
        } else {
          // Fallback: if no specific error element found, scroll to the contact form section
          const contactSection = document.querySelector('.fees-title');
          if (contactSection && contactSection.textContent.includes('Contact')) {
            const headerOffset = 100;
            const elementPosition = contactSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        }
      };

      attemptScroll();
    }, 150); // Slightly longer initial delay for dynamic content
  }

  /**
   * Scrolls to the payment section when Stripe validation errors occur
   */
  function scrollToPaymentSection() {
    // Wait for next tick to ensure DOM is updated
    setTimeout(() => {
      // Try to find the payment element first
      const paymentElementDOM = document.querySelector('#payment-element');

      if (paymentElementDOM) {
        // Calculate scroll position with some offset for better UX
        const headerOffset = 60; // Slightly larger offset to show the "Payment" title
        const elementPosition = paymentElementDOM.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        // Smooth scroll to the payment section
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      } else {
        // Fallback: scroll to payment section title
        const paymentSection = Array.from(document.querySelectorAll('.fees-title'))
          .find((el) => el.textContent.includes('Payment'));

        if (paymentSection) {
          const headerOffset = 20;
          const elementPosition = paymentSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }
    }, 100);
  }

  return {
    scrollToValidationError,
    scrollToPaymentSection,
  };
}
